import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { doc, getDoc, getFirestore } from "firebase/firestore";
import Modal from "components/Modal";
import ColorCard from "components/ColorCard";
import { COLORS, FONTSIZE } from "helpers/designConstants";
import { filterLegacyWarehouses } from "./warehouseFilters";

/**
 * Purely informational modal showing full product detail for a stock issue.
 *
 * Reuses the ColorCard component from the project builder so the visuals
 * match EXACTLY. Fetches the project's Firestore doc on open to pick up its
 * selected warehouses (in DB order) and preferred currency.
 *
 * `product` is the transformed shape from getProductsDetailById():
 *   { id, itemNumber, name, color, gender, isClearance, imageURL, productURL,
 *     sizes: [{ name, sku }], price: { CAD, USD, GBP, EURO },
 *     currentInventory: { [warehouseCode]: { [sizeName]: qty } },
 *     incomingInventory: { [warehouseCode]: [{ date, incomingBySize }] },
 *     updatedAt, ... }
 *
 * ColorCard expects liveInventory keyed by sku:
 *   { [sku]: { [warehouseCode]: { onHand: qty } } }
 * — so we adapt product.currentInventory into that shape below.
 */
export default function InventoryDialog({
  show,
  onClose,
  product,
  projectFirebaseId,
  oosSizes = [],
}) {
  const [projectDoc, setProjectDoc] = useState(null);
  const [loadingProject, setLoadingProject] = useState(false);

  useEffect(() => {
    if (!show) {
      setProjectDoc(null);
      setLoadingProject(false);
      return;
    }
    if (!projectFirebaseId) {
      setProjectDoc(null);
      setLoadingProject(false);
      return;
    }
    let cancelled = false;
    setLoadingProject(true);
    const db = getFirestore();
    (async () => {
      try {
        const snap = await getDoc(doc(db, "projects", projectFirebaseId));
        if (cancelled) return;
        setProjectDoc(snap.exists() ? snap.data() : null);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Failed to fetch project doc for inventory dialog", e);
        if (!cancelled) setProjectDoc(null);
      } finally {
        if (!cancelled) setLoadingProject(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [show, projectFirebaseId]);

  const liveInventory = useMemo(() => {
    if (!product) return {};
    const result = {};
    for (const size of product.sizes || []) {
      result[size.sku] = {};
      for (const [wh, sizes] of Object.entries(
        product.currentInventory || {},
      )) {
        const qty = sizes?.[size.name];
        if (qty != null) {
          result[size.sku][wh] = { onHand: qty };
        }
      }
    }
    return result;
  }, [product]);

  const title = product
    ? `${product.itemNumber ?? ""} — ${product.name ?? ""}`
    : "Product detail";

  let body;
  if (!product) {
    body = <Empty>Product detail unavailable.</Empty>;
  } else if (loadingProject) {
    body = <Empty>Loading project context…</Empty>;
  } else if (!projectDoc) {
    body = <Empty>Project context unavailable.</Empty>;
  } else {
    body = (
      <ColorCard
        productData={product}
        selectedWarehouses={filterLegacyWarehouses(projectDoc.warehouses || [])}
        currency={projectDoc.currency || "$ CAD"}
        liveInventory={liveInventory}
        isLoadingInventory={false}
        showOversold={true}
        isSelectable={false}
        borderless={true}
        highlightedSizes={oosSizes}
      />
    );
  }

  return (
    <Modal
      show={show}
      onClose={onClose}
      title={title}
      maxWidth="1160px"
      maxHeight="90vh"
    >
      <DialogBody>{body}</DialogBody>
    </Modal>
  );
}

const DialogBody = styled.div`
  padding: 12px;
  /* vh-anchored so scroll works regardless of the parent Modal's grid
     sizing quirks (Modal's Content uses overflow:hidden and a 1fr grid row
     with default min-height:auto, which can otherwise swallow our scroll). */
  max-height: calc(90vh - 80px);
  overflow-y: auto;
  overflow-x: hidden;
`;

const Empty = styled.div`
  padding: 30px;
  text-align: center;
  color: ${COLORS.text};
  font-size: ${FONTSIZE.small};
`;
